/**
 * ProducerSetupPage – editorial onboarding for new hosts.
 * Clean layout (no sidebar/topbar), subtle timeline, autosave.
 * Route: /app/producer/farm/:farmId/setup
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, ArrowRight, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { GroupedOfferingSelector } from '@/components/producer/GroupedOfferingSelector';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import { useProducerOfferings } from '@/hooks/useProducerOfferings';
import { isSupportPreviewActive } from '@/lib/supportPreview';
import { useHostState } from '@/hooks/useHostState';
import { useProducerById } from '@/hooks/useMyProducers';
import { useMediaAssets } from '@/hooks/useMediaAssets';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { MediaImageManager } from '@/components/media/MediaImageManager';
import { useDraftSessionId } from '@/hooks/useDraftSessionId';
import { usePublishProducer } from '@/hooks/usePublishProducer';
import { ProducerSetupVisibilityCard } from '@/components/producer/ProducerSetupVisibilityCard';
import { useQuery } from '@tanstack/react-query';
import { track } from '@/lib/analytics';

/* ─── Constants ─── */

/* ─── Types ─── */

interface PlaceData {
  id: string;
  name: string;
  slug: string;
  municipality: string | null;
  description_short: string | null;
  place_core: Record<string, any> | null;
  status: string | null;
}

/* ─── Helpers ─── */

function computeOnboardingStatus(
  producerDesc: string | null | undefined,
  mediaCount: number,
  offerings: string[],
) {
  const hasDescription = Boolean(producerDesc?.trim() && producerDesc.trim().length >= 10);
  const hasImages = mediaCount >= 1;
  const hasOfferings = offerings.length >= 1;
  return { hasDescription, hasImages, hasOfferings, isComplete: hasDescription && hasImages && hasOfferings };
}

/** Check if description is "meaningfully complete" (>=20 chars trimmed) */
function isDescriptionComplete(desc: string) {
  return desc.trim().length >= 20;
}

/* ─── Timeline step (collapsible) ─── */

function TimelineStep({
  done,
  active,
  highlighted,
  open,
  onToggle,
  title,
  children,
  isLast,
}: {
  done: boolean;
  active: boolean;
  highlighted?: boolean;
  open: boolean;
  onToggle: () => void;
  title: string;
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-4 sm:gap-6">
      {/* Timeline indicator */}
      <div className="flex flex-col items-center pt-1.5">
        <div
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
            done
              ? 'bg-primary/20 border-primary'
              : active || highlighted
                ? 'border-primary bg-background'
                : 'border-border bg-background'
          }`}
        >
          {done && <Check className="h-3 w-3 text-primary" />}
          {active && !done && <div className="w-2 h-2 rounded-full bg-primary" />}
        </div>
        {!isLast && (
          <div className={`w-px flex-1 min-h-[24px] ${done ? 'bg-primary/30' : 'bg-border'}`} />
        )}
      </div>

      {/* Content */}
      <div className="pb-8 sm:pb-10 flex-1 min-w-0">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 text-left w-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm -ml-0.5 pl-0.5"
          aria-expanded={open}
        >
          <h3 className={`text-base font-medium mb-0 transition-colors ${
            active || done || highlighted ? 'text-foreground' : 'text-muted-foreground'
          } ${highlighted && !active && !done ? 'text-foreground/80' : ''}`}>
            {title}
          </h3>
          <ChevronDown
            className={`h-3.5 w-3.5 text-muted-foreground/50 shrink-0 transition-transform duration-200 group-hover:text-muted-foreground ${
              open ? '' : '-rotate-90'
            }`}
          />
        </button>
        {open && (
          <div className="mt-2">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Autosave status indicator ─── */

function SaveStatus({ status }: { status: 'idle' | 'saving' | 'saved' }) {
  if (status === 'idle') return null;
  return (
    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
      {status === 'saving' ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          Sparar…
        </>
      ) : (
        <>
          <Check className="h-3 w-3 text-primary" />
          Sparat
        </>
      )}
    </span>
  );
}

/* ─── Main Page ─── */

export default function ProducerSetupPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { hostState, isLoading: hostLoading } = useHostState();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const queryClient = useQueryClient();
  const draftSessionId = useDraftSessionId(`producer-media-${farmId ?? 'new'}`);

  // Fetch linked place
  const placeId = producer?.place_id ?? null;
  const { data: place } = useQuery<PlaceData | null>({
    queryKey: ['setup-place', placeId],
    queryFn: async () => {
      if (!placeId) return null;
      const { data, error } = await supabase
        .from('places')
        .select('id, name, slug, municipality, description_short, place_core, status')
        .eq('id', placeId)
        .maybeSingle();
      if (error) throw error;
      return data as PlaceData | null;
    },
    enabled: !!placeId,
  });

  // Media assets count
  const { data: coverAssets } = useMediaAssets('producer', farmId, undefined, null);
  const { data: placeAssets } = useMediaAssets('place', placeId, undefined, null);
  const mediaCount = (coverAssets?.length ?? 0) + (placeAssets?.length ?? 0);

  // Media upload hooks
  const coverUpload = useMediaUpload({
    ownerType: 'producer',
    ownerId: farmId ?? null,
    draftSessionId: !farmId ? draftSessionId : undefined,
    kind: 'cover',
    maxFiles: 1,
  });
  const galleryUpload = useMediaUpload({
    ownerType: 'producer',
    ownerId: farmId ?? null,
    draftSessionId: !farmId ? draftSessionId : undefined,
    kind: 'gallery',
    maxFiles: 15,
  });

  // Local state
  const [description, setDescription] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const initialized = useRef(false);
  const [descFocused, setDescFocused] = useState(false);

  // ─── Offerings state (from normalized producer_offerings table) ───
  const {
    offerings,
    offeringRows,
    isLoading: offeringsLoading,
    saveOfferings: saveOfferingsToDb,
  } = useProducerOfferings(farmId);
  const [localOfferings, setLocalOfferings] = useState<string[]>([]);
  const [offeringsOther, setOfferingsOther] = useState('');
  const offeringsInitialized = useRef(false);
  const offeringsDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Initialize offerings from canonical producer_offerings once query is loaded.
  // Legacy fallback is only used when no canonical rows exist.
  useEffect(() => {
    if (!producer || offeringsLoading || offeringsInitialized.current) return;

    const other: string = (producer as any).offering_note ?? '';
    setOfferingsOther(other);

    if (offeringRows.length > 0) {
      setLocalOfferings(offerings);
    } else {
      const fallbackKeys: string[] = [];
      if ((producer as any).has_farm_shop) fallbackKeys.push('farm_shop');
      if ((producer as any).has_farm_sale_alcohol) fallbackKeys.push('other_farm_sales');
      setLocalOfferings(fallbackKeys);
    }

    offeringsInitialized.current = true;
  }, [producer, offeringsLoading, offeringRows.length, offerings]);

  // ─── Collapsible step state ───
  const [openSteps, setOpenSteps] = useState<Record<number, boolean>>({ 1: true, 2: false, 3: false });
  const autoExpandedRef = useRef<Record<number, boolean>>({});

  const toggleStep = useCallback((step: number) => {
    setOpenSteps(prev => ({ ...prev, [step]: !prev[step] }));
    track('place_editor_step_opened', { step, surface: 'producer_portal' });
  }, []);

  // Initialize description from producer
  useEffect(() => {
    if (!initialized.current && producer) {
      setDescription(producer.short_description || '');
      initialized.current = true;
    }
  }, [producer]);

  // Autosave description
  const saveDescription = useCallback(async (value: string) => {
    if (!farmId || !producer || isSupportPreviewActive()) return;
    setSaveStatus('saving');
    try {
      const { error } = await supabase
        .from('producers')
        .update({ short_description: value.trim() })
        .eq('id', farmId);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['producer', farmId] });
      queryClient.invalidateQueries({ queryKey: ['producer-capabilities', farmId] });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('idle');
      toast.error('Kunde inte spara beskrivningen.');
    }
  }, [farmId, producer, queryClient]);

  const handleDescriptionChange = useCallback((value: string) => {
    setDescription(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveDescription(value), 700);
  }, [saveDescription]);

  // Autosave offerings (to normalized table + offering_note on producers)
  const saveOfferings = useCallback(async (nextOfferings: string[], nextOther: string) => {
    if (!farmId || !producer || isSupportPreviewActive()) return;
    setSaveStatus('saving');
    try {
      // Save offerings to normalized table
      await saveOfferingsToDb(nextOfferings);
      // Save offering_note to producers table
      const { error } = await supabase
        .from('producers')
        .update({ offering_note: nextOther.trim() || null })
        .eq('id', farmId);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['producer', farmId] });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('idle');
      toast.error('Kunde inte spara.');
    }
  }, [farmId, producer, queryClient, saveOfferingsToDb]);


  // ─── Progressive reveal: auto-expand next step ───
  const descComplete = isDescriptionComplete(description);

  useEffect(() => {
    if (descComplete && !autoExpandedRef.current[2]) {
      autoExpandedRef.current[2] = true;
      setOpenSteps(prev => ({ ...prev, 2: true }));
      track('place_editor_step_completed', { step: 1, surface: 'producer_portal' });
    }
  }, [descComplete]);

  useEffect(() => {
    if (mediaCount >= 1 && !autoExpandedRef.current[3]) {
      autoExpandedRef.current[3] = true;
      setOpenSteps(prev => ({ ...prev, 3: true }));
      track('place_editor_step_completed', { step: 2, surface: 'producer_portal' });
    }
  }, [mediaCount]);

  useEffect(() => {
    if (localOfferings.length >= 1) {
      track('place_editor_step_completed', { step: 3, surface: 'producer_portal' });
    }
  }, [localOfferings.length]);

  // Publish
  const publishMutation = usePublishProducer();
  const handlePublish = async () => {
    if (!farmId) return;
    try {
      await publishMutation.mutateAsync(farmId);
    } catch {
      // Error toast handled by usePublishProducer onError
    }
  };

  // Onboarding status
  const status = computeOnboardingStatus(description, mediaCount, localOfferings);

  // Loading state
  const loading = hostLoading || producerLoading;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Guard: must be active host
  if (hostState.kind !== 'active_host') {
    return <Navigate to="/app/customer" replace />;
  }

  // Determine next incomplete step for sticky footer
  const nextIncompleteStep = !status.hasDescription ? 1 : !status.hasImages ? 2 : !status.hasOfferings ? 3 : null;

  const handleContinue = () => {
    if (nextIncompleteStep) {
      setOpenSteps(prev => ({ ...prev, [nextIncompleteStep]: true }));
      // Scroll to the step (gentle)
      const el = document.getElementById(`setup-step-${nextIncompleteStep}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Minimal header */}
      <header className="border-b border-border/40 -mx-5 -mt-10 mb-8">
        <div className="max-w-[720px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-end">
          <Link
            to={`/app/producer/farm/${farmId}`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            Till portalen
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pb-0">
        {/* Hero */}
        <div className="mb-10 sm:mb-14">
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
            Forma din plats
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-[520px] mt-3">
            Tre saker hjälper besökare att förstå vad som möter dem.
            <br />
            Gör det i din takt — allt sparas automatiskt.
          </p>
          <div className="mt-2">
            <SaveStatus status={saveStatus} />
          </div>
        </div>

        {/* Timeline */}
        <div>
          {/* Section 1: Description */}
          <div id="setup-step-1">
            <TimelineStep
              done={status.hasDescription}
              active={!status.hasDescription}
              open={!!openSteps[1]}
              onToggle={() => toggleStep(1)}
              title="Vad möter man när man kommer hit?"
            >
              <p className="text-xs text-muted-foreground mb-3">
                Skriv med dina egna ord. Det behöver inte vara perfekt.
              </p>
              <Textarea
                value={description}
                onChange={e => handleDescriptionChange(e.target.value)}
                onFocus={() => setDescFocused(true)}
                onBlur={() => setDescFocused(false)}
                placeholder="Beskriv platsen med några meningar…"
                className="min-h-[100px] resize-none"
                maxLength={500}
              />
              <p className={`text-xs text-right mt-1 transition-opacity duration-300 ${
                descFocused || description.length > 0
                  ? 'text-muted-foreground/40 opacity-100'
                  : 'opacity-0'
              }`}>
                {description.length}/500
              </p>
            </TimelineStep>
          </div>

          {/* Section 2: Images */}
          <div id="setup-step-2">
            <TimelineStep
              done={status.hasImages}
              active={status.hasDescription && !status.hasImages}
              highlighted={descComplete && !status.hasImages}
              open={!!openSteps[2]}
              onToggle={() => toggleStep(2)}
              title="Visa platsen"
            >
              <p className="text-xs text-muted-foreground mb-3">
                En bild hjälper besökare att förstå platsen direkt.
              </p>
              <MediaImageManager
                ownerType="producer"
                ownerId={farmId}
                producerId={farmId}
                draftSessionId={draftSessionId}
                coverUpload={coverUpload}
                galleryUpload={galleryUpload}
                helperText=""
              />
              {mediaCount > 0 && (
                <p className="text-xs text-primary mt-2 inline-flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  {mediaCount === 1 ? '1 bild tillagd' : `${mediaCount} bilder tillagda`}
                </p>
              )}
            </TimelineStep>
          </div>

          {/* Section 3: Offerings (producer-oriented) */}
          <div id="setup-step-3">
            <TimelineStep
              done={status.hasOfferings}
              active={status.hasDescription && status.hasImages && !status.hasOfferings}
              highlighted={status.hasImages && !status.hasOfferings}
              open={!!openSteps[3]}
              onToggle={() => toggleStep(3)}
              title="Vad erbjuder du här?"
              isLast
            >
              <p className="text-xs text-muted-foreground mb-4">
                Välj det som passar. Du kan ändra senare.
              </p>

              <GroupedOfferingSelector
                offerings={localOfferings}
                onOfferingsChange={(next) => {
                  setLocalOfferings(next);
                  if (offeringsDebounceRef.current) clearTimeout(offeringsDebounceRef.current);
                  offeringsDebounceRef.current = setTimeout(() => saveOfferings(next, offeringsOther), 500);
                }}
                otherText={offeringsOther}
                onOtherTextChange={(text) => {
                  setOfferingsOther(text);
                  if (offeringsDebounceRef.current) clearTimeout(offeringsDebounceRef.current);
                  offeringsDebounceRef.current = setTimeout(() => saveOfferings(localOfferings, text), 700);
                }}
              />
            </TimelineStep>
          </div>
        </div>

        {/* Footer — Visibility module */}
        <div className="mt-4 pt-6 border-t border-border/40">
          <ProducerSetupVisibilityCard
            placeStatus={place?.status ?? 'draft'}
            producerStatus={producer?.status ?? 'draft'}
            placeName={place?.name ?? null}
            placeSlug={place?.slug ?? null}
            placeMunicipality={place?.municipality ?? null}
            producerSlug={producer?.slug}
            hasPlace={!!producer?.place_id}
            onPublishProducer={handlePublish}
            publishPending={publishMutation.isPending}
          />
        </div>

        {/* Sticky bottom action bar — always visible */}
        <div
          className="sticky bottom-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur-sm -mx-5 px-5 py-3 mt-6"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        >
          {nextIncompleteStep ? (
            <Button onClick={handleContinue} className="w-full">
              Fortsätt
            </Button>
          ) : (
            <Button asChild variant="default" className="w-full">
              <Link to={`/app/producer/farm/${farmId}`}>
                Till portalen
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          )}
        </div>
      </main>
    </>
  );
}
